import React, { useState, useEffect } from "react";
import axios from "axios";
import Swal from "sweetalert2";
import { Modal, Button, Form, InputGroup, Spinner, Alert } from "react-bootstrap";
import { BASE_URL } from "../../Config";
import "bootstrap-icons/font/bootstrap-icons.css";

/**
 * Company can reset password only for a supervisor/agent already loaded
 * from their own company lists (email is locked to the selected row).
 */
const ResetPasswordModal = ({
  show,
  onHide,
  email,
  displayName,
  roleLabel = "user",
}) => {
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!show) {
      setPassword("");
      setConfirmPassword("");
      setShowPassword(false);
      setLoading(false);
    }
  }, [show]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!email) {
      Swal.fire(
        "Error",
        "Email is not available for this user. Password cannot be reset.",
        "error"
      );
      return;
    }

    if (!password || password.length < 6) {
      Swal.fire("Error", "Password must be at least 6 characters.", "error");
      return;
    }

    if (password !== confirmPassword) {
      Swal.fire("Error", "Password and confirmation do not match.", "error");
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(
        `${BASE_URL}RetailPasswordReset`,
        { email, password },
        {
          headers: {
            accept: "text/plain",
            "Content-Type": "application/json",
          },
        }
      );

      const data = response.data;
      const ok =
        data?.responseCode === "0000" ||
        data?.success === true ||
        (typeof data === "string" && /success/i.test(data));

      Swal.fire({
        icon: ok ? "success" : "error",
        title: ok ? "Password Updated" : "Unable to Reset Password",
        text:
          (typeof data === "string" ? data : data?.message) ||
          (ok
            ? "Password has been updated successfully."
            : "Password reset failed. Please try again."),
      });

      if (ok) onHide();
    } catch (error) {
      console.error("RetailPasswordReset error:", error);
      Swal.fire(
        "Error",
        error.response?.data?.message ||
          "Unable to reset password. Please try again.",
        "error"
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal show={show} onHide={onHide} centered>
      <Modal.Header closeButton>
        <Modal.Title>
          <i className="bi bi-key me-2" />
          Reset Password
        </Modal.Title>
      </Modal.Header>
      <Form onSubmit={handleSubmit}>
        <Modal.Body>
          <Alert variant="light" className="border small mb-3">
            Resetting password for {roleLabel}
            {displayName ? (
              <>
                : <strong>{displayName}</strong>
              </>
            ) : null}
          </Alert>

          <Form.Group className="mb-3">
            <Form.Label className="small fw-semibold">Email</Form.Label>
            <Form.Control type="email" value={email || ""} readOnly plaintext={false} disabled />
            <Form.Text muted>
              Password can only be changed for your company {roleLabel}s.
            </Form.Text>
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label className="small fw-semibold">New Password</Form.Label>
            <InputGroup>
              <Form.Control
                type={showPassword ? "text" : "password"}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter new password"
                required
                autoComplete="new-password"
              />
              <Button
                variant="outline-secondary"
                type="button"
                onClick={() => setShowPassword((v) => !v)}
              >
                <i className={`bi ${showPassword ? "bi-eye-slash" : "bi-eye"}`} />
              </Button>
            </InputGroup>
          </Form.Group>

          <Form.Group>
            <Form.Label className="small fw-semibold">Confirm Password</Form.Label>
            <Form.Control
              type={showPassword ? "text" : "password"}
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              placeholder="Re-enter new password"
              required
              autoComplete="new-password"
            />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={onHide} disabled={loading}>
            Cancel
          </Button>
          <Button type="submit" variant="primary" disabled={loading || !email}>
            {loading ? (
              <>
                <Spinner animation="border" size="sm" className="me-1" />
                Updating...
              </>
            ) : (
              <>
                <i className="bi bi-check2 me-1" />
                Update Password
              </>
            )}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

export default ResetPasswordModal;
